import { all, delay, select, takeEvery } from 'redux-saga/effects';
import { authActions } from 'store/authSlice/authSlice';
import { desksActions } from 'store/desksSlice/desksSlice';
import { generalActions } from 'store/generalSlice/generalSlice';
import { featureFlagActions } from 'store/featureFlag/featureFlagSlice';
import { selectIsActivityEventsEnabled } from 'store/featureFlag/featureFlagSelector';
import { selectCurrentDesk } from 'store/desksSlice/desksSelectors';
import { fenixYmsActions } from 'store/fenixYmsSlice/fenixYmsSlice';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { faceMatchActions } from 'store/faceMatchSlice/faceMatchSlice';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import { configureActivityQueue, flushActivityQueue, isActivityQueueEnabled } from 'services/activityTracking/activityEventQueue';
import {
    recordFieldReviewCompleted,
    resetOperatorStateTracker,
    snapshotOperatorState,
    startOperatorHeartbeat,
    stopOperatorHeartbeat,
    transitionOperatorState,
} from 'services/activityTracking/operatorStateTracker';
import {
    closeAll,
    closeTransactionPeriods,
    hasOpenFieldPeriodFor,
    markOpenFieldDirty,
    recordAutomationValueApplied,
    recordManualValueApplied,
    switchTo,
} from 'services/activityTracking/focusPeriodTracker';
import {
    beginYmsExportAttempt,
    cancelYmsExportAttempt,
    completeYmsExportAttempt,
    confirmYmsExportAttempt,
    dropYmsExportAttempt,
    failYmsExportAttempt,
    resetYmsExportTracker,
} from 'services/activityTracking/ymsExportTracker';
import {
    dropFaceMatchAttempt,
    recordFaceMatchFailed,
    recordFaceMatchRequested,
    recordFaceMatchSucceeded,
    resetFaceMatchTracker,
} from 'services/activityTracking/faceMatchTracker';
import {
    ACTIVITY_END_REASON,
    ACTIVITY_EXTERNAL_MANUAL_VALUE_APPLIED_ACTION,
    ACTIVITY_FIELD_REVIEW_RESULT,
    ACTIVITY_OPERATOR_SNAPSHOT_REASON,
    ACTIVITY_OPERATOR_STATE,
    ACTIVITY_PAGE_KEY,
    ACTIVITY_WINDOW_FOCUSED_ACTION,
} from 'utils/activityTracking/constants';
import { WS_CONNECTION_STATE } from 'config';
import { AUTOMATION_SOURCE, PROPERTY_NOT_FOUND } from 'utils/constants';
import { readFromObj } from 'utils/utils';
import { buildFieldTypeMap, resolveActivityFieldType } from 'utils/activityTracking/fieldType';
import { USER_EDIT_SOURCE, resolveInputSource } from 'utils/activityTracking/inputSource';
import { resolveYmsExportAttemptNumber } from 'utils/activityTracking/ymsExport';
import {
    VALUE_APPLIED_EXCLUDED_FIELDS,
    autoSuggestionActions,
    bulkAutomationActions,
    directValueWriteActions,
    isUserEditAction,
    readAutoSuggestionTarget,
    readBulkAutomationTarget,
    readDirectWrite,
} from 'store/activityTracking/valueAppliedSeams';

/**
 * The store-side seams of the agent activity event stream (BEAST-13036).
 *
 * Thin adapters only: each watcher translates a store event into a call on the focus period state
 * machine and nothing more. The period bookkeeping, the envelope and the transport all live
 * outside `store/`, so this file holds no telemetry logic of its own.
 *
 * There is no slice here on purpose - the session id and the sequence are module state, not store
 * state, and must not be serialized, rehydrated or shared across windows.
 */

/**
 * Wraps a watcher so telemetry can neither crash the app nor do work the flag has switched off.
 *
 * TWO SEPARATE REASONS, BOTH LOAD-BEARING.
 *
 * The crash one: `rootSaga` is `all([call(activityTrackingSaga), ...])` and `sagaMiddleware.run`
 * is called once with no `onError` and no restart, so ONE uncaught throw in any watcher here takes
 * down the entire saga layer for the page - submit, transactions, desks, all of it. Every other
 * layer of this feature is wrapped precisely so telemetry breaking stays invisible; the watchers
 * were the one gap.
 *
 * The flag one: with the flag off the queue no-ops, but the watchers still ran - `delay(0)`, three
 * selects and a `transactions.find()` on every programmatic `setFieldValue`, and a walk over a
 * whole form's sources on every automation pass. That is rollout risk with none of the benefit,
 * which is the opposite of what a kill switch is for.
 *
 * Applied at registration rather than inside each worker so the workers stay individually
 * testable, and so a new watcher cannot be added without passing through here.
 */
/** Containment only. Used directly for the flag watcher, which must run while the gate is shut. */
function safeWorker(worker) {
    return function* wrapped(action) {
        try {
            const running = worker(action);
            // Checks for a GENERATOR specifically, not merely something iterable: a plain worker
            // that returned a string or an array would otherwise have its characters or items
            // pushed into the saga one by one as effects.
            if (typeof running?.next === 'function') yield* running;
        } catch (error) {
            console.warn('activityTracking: a store seam failed and was contained.', error);
        }
    };
}

/** Containment plus the gate, composed rather than repeated, for every other watcher. */
function gatedWorker(worker) {
    const contained = safeWorker(worker);
    return function* wrapped(action) {
        if (!isActivityQueueEnabled()) return;
        yield* contained(action);
    };
}

const selectTransactionsList = (state) => state.transactions?.transactions ?? [];
const selectActiveTabId = (state) => state.transactions?.activeTab;
const selectIsSplitViewMode = (state) => !!state.transactions?.splitViewMode;
const selectSplitViewActiveTransactionId = (state) => state.transactions?.splitViewActiveTransactionId ?? null;
const selectCurrentDeskId = (state) => state.desks?.currentDesk?.id ?? null;
const selectFormValueSources = (state, formId) => state.forms?.forms?.[formId]?.form?.fieldValueSources ?? {};
const selectFormValues = (state, formId) => state.forms?.forms?.[formId]?.form?.values ?? {};
const selectFormConfigFields = (state, formId) => state.forms?.forms?.[formId]?.formConfig?.fields ?? [];
const selectFormAutomation = (state, formId) => state.forms?.forms?.[formId]?.form?.automation ?? {};
const selectFormUserEdited = (state, formId) => state.forms?.forms?.[formId]?.form?.userEdited ?? {};
const selectFormTransactionData = (state, formId) => state.forms?.forms?.[formId]?.transactionData ?? null;
const selectUser = (state) => state.auth?.user ?? null;
const selectStompConnectionState = (state) => state.transactions?.stompBrokerConnectionState;
const selectFenixYmsEntry = (state, transactionId) => state.fenixYms?.byTransactionId?.[transactionId] ?? null;

function* readSafeOperatorState() {
    const [user, deskId, transactions, connectionState] = [
        yield select(selectUser),
        yield select(selectCurrentDeskId),
        yield select(selectTransactionsList),
        yield select(selectStompConnectionState),
    ];

    if (!user) return null;
    if (typeof document !== 'undefined' && (document.hidden || !document.hasFocus())) return ACTIVITY_OPERATOR_STATE.OUTSIDE_APP;
    if (connectionState !== WS_CONNECTION_STATE.CONNECTED || !deskId || transactions.length > 0) return ACTIVITY_OPERATOR_STATE.UNKNOWN;
    return ACTIVITY_OPERATOR_STATE.READY_IDLE;
}

/**
 * `location_id`, `gate_id` and `desk_id` are the AIQ native numeric ids off the transaction object,
 * never names. `lane_id` is filled from `gate_id` in the envelope factory, because in AIQ a lane is
 * a gate.
 *
 * `desk_id` is always sent when we have it: the backend's server-side desk lookup is a database hit
 * on a chatty endpoint and ships disabled behind a flag, so a missing `desk_id` from us means a
 * null in the fact store. The current desk is the fallback when the transaction carries none.
 */
function* readOperationalContext(transaction) {
    const currentDeskId = yield select(selectCurrentDeskId);
    return {
        locationId: transaction?.locationId ?? null,
        gateId: transaction?.gateId ?? null,
        deskId: transaction?.deskId ?? currentDeskId ?? null,
    };
}

/**
 * Which of the five page contexts a period belongs to.
 *
 * Split view is itself a tab, so `splitViewMode` IS split-view mode and no agent is ever in both.
 * A reopened transaction is separated out because that is how a query later tells apart the three
 * ways an agent can be looking at a transaction.
 */
function resolvePageKey({ isSplitViewMode, transaction }) {
    if (isSplitViewMode) return ACTIVITY_PAGE_KEY.TRANSACTIONS_SPLIT_VIEW;
    if (transaction?.tabId === ACTIVITY_PAGE_KEY.REOPENED_TRANSACTION) return ACTIVITY_PAGE_KEY.REOPENED_TRANSACTION;
    return ACTIVITY_PAGE_KEY.TRANSACTION;
}

/**
 * A tab holds a work object if and only if a transaction exists whose `tabId` equals it.
 *
 * DO NOT use `isFinite(tabId)` for this. The four non-transaction tab ids are strings, and ordinary
 * transaction tab ids are numbers, which is why `Tab.jsx` gets away with it - but
 * `handleNewTransaction` assigns a REOPENED transaction the string `'reopenedTransaction'` as its
 * own `tabId`. It is a real transaction an agent really works in, and `isFinite` says false. This
 * store lookup needs no id-type heuristic, handles the reopened tab for free, and excludes Desks,
 * the Video Wall and the split view tab by construction.
 */
function findTransactionByTabId(transactions, tabId) {
    return transactions.find((transaction) => transaction?.tabId === tabId) ?? null;
}

/**
 * The single authority for the queue's enabled state.
 *
 * Two gates, both required (BEAST-13036 global flag AND the BEAST-13284 per-desk setting): the
 * global `/feature-flag/all` flag AND `currentDesk.activityEventsEnabled` for the desk the agent is
 * working on right now. Both default OFF, so until the flag arrives and the agent is on a tracked
 * desk the queue holds nothing and makes zero requests - a missing or erroring endpoint, or no desk,
 * cannot affect AIQ startup, transaction entry or submit.
 *
 * Read from the store, never from an action payload, so it is correct no matter which input fired -
 * a flag change, a desk change, or an admin toggling a desk's setting. Turning OFF here also clears
 * the pending queue (in `configureActivityQueue`), so a disabled desk buffers nothing for later.
 */
export function* reconfigureActivityQueueGate() {
    const globalEnabled = yield select(selectIsActivityEventsEnabled);
    const currentDesk = yield select(selectCurrentDesk);
    configureActivityQueue({ enabled: globalEnabled && !!currentDesk?.activityEventsEnabled });
}

export function* onActivityFeatureFlagData() {
    // Recompute the combined gate (global AND current desk) first, then drive operator-state off the
    // global flag: the heartbeat/snapshot follow global availability, while per-desk suppression is
    // handled in the queue via isActivityQueueEnabled().
    yield* reconfigureActivityQueueGate();
    const globalEnabled = yield select(selectIsActivityEventsEnabled);
    if (!globalEnabled) {
        stopOperatorHeartbeat();
        return;
    }

    const state = yield* readSafeOperatorState();
    if (!state) return;
    snapshotOperatorState(state, ACTIVITY_OPERATOR_SNAPSHOT_REASON.RECOVERY);
    startOperatorHeartbeat();
}

export function* onAuthenticatedUserChanged({ payload: user }) {
    if (!user) {
        stopOperatorHeartbeat();
        return;
    }
    const state = yield* readSafeOperatorState();
    if (!state) return;
    snapshotOperatorState(state, ACTIVITY_OPERATOR_SNAPSHOT_REASON.LOGIN);
    startOperatorHeartbeat();
}

export function* onCurrentDeskChanged({ payload: desk }) {
    const user = yield select(selectUser);
    if (!user) return;

    if (!desk?.id) {
        transitionOperatorState(ACTIVITY_OPERATOR_STATE.UNKNOWN, 'desk_left');
        return;
    }

    const state = yield* readSafeOperatorState();
    snapshotOperatorState(state ?? ACTIVITY_OPERATOR_STATE.UNKNOWN, ACTIVITY_OPERATOR_SNAPSHOT_REASON.DESK_JOIN);
}

export function* onStompConnectionStateChanged({ payload: connectionState }) {
    const user = yield select(selectUser);
    if (!user) return;

    if (connectionState !== WS_CONNECTION_STATE.CONNECTED) {
        transitionOperatorState(ACTIVITY_OPERATOR_STATE.UNKNOWN, 'connection_lost');
        return;
    }

    const state = yield* readSafeOperatorState();
    snapshotOperatorState(state ?? ACTIVITY_OPERATOR_STATE.UNKNOWN, ACTIVITY_OPERATOR_SNAPSHOT_REASON.RECONNECT);
}

export function* onOperatorWorkStateChanged() {
    const state = yield* readSafeOperatorState();
    if (state) transitionOperatorState(state, 'work_state_changed');
}

export function* onSharedWorkerStateChanged({ payload: disconnected }) {
    const user = yield select(selectUser);
    if (!user) return;
    if (disconnected) {
        transitionOperatorState(ACTIVITY_OPERATOR_STATE.UNKNOWN, 'connection_lost');
        return;
    }
    const state = yield* readSafeOperatorState();
    snapshotOperatorState(state ?? ACTIVITY_OPERATOR_STATE.UNKNOWN, ACTIVITY_OPERATOR_SNAPSHOT_REASON.RECOVERY);
}

export function* onWindowFocused() {
    yield* onActiveTabChanged();
    const state = yield* readSafeOperatorState();
    if (state) transitionOperatorState(state, 'window_focused');
}

/**
 * Tab activation. Instrumented on the STORE, not on the click handler in `Tabs.jsx`, because only
 * one of the ways `activeTab` moves is a click.
 *
 * BUT `setActiveTab` IS NOT THE ONLY FUNNEL, contrary to the ticket's finding 1. Four reducers in
 * `transactionsSlice` assign `state.activeTab` and only one of them is `setActiveTab`:
 *
 *   - `setActiveTab` - the tab click handler and every explicit dispatch, including the one the
 *     `closeTransactionPanel` saga makes when all panels close.
 *   - `handleNewTransaction` - a FIRST or REOPENED transaction arriving moves the tab directly,
 *     with no `setActiveTab` dispatch anywhere. Missing this leaves the previous transaction's
 *     period open while the agent works in the new one, which is exactly the silent failure
 *     finding 1 warns about, just at a different seam than it names.
 *   - `setVideoWallCameras` - moves the tab to the video wall, but only from Desks, so no period
 *     can be open when it fires. Watched anyway, because relying on that guard staying in place
 *     is not worth the coupling.
 *   - `removeTransaction` - reassigns the tab, but NOTHING in the app dispatches it. Dead code and
 *     not a seam; deliberately not watched.
 *
 * This worker reads `activeTab` back out of the store rather than from the action payload, so it is
 * correct for any action that moved it, and `switchTo` is re-entrant on an already-open transaction,
 * so a watched action that did NOT move the tab costs nothing.
 */
export function* onActiveTabChanged() {
    const [transactions, activeTab, isSplitViewMode] = [
        yield select(selectTransactionsList),
        yield select(selectActiveTabId),
        yield select(selectIsSplitViewMode),
    ];

    const transaction = findTransactionByTabId(transactions, activeTab);

    if (!transaction) {
        // Desks and the Video Wall hold no work object, so the current period ends and nothing
        // starts. The split view tab also ends the current period - but it is a mode, not a dead
        // end: `onSplitViewActiveTransactionChanged` and the field listener open a period there as
        // soon as a card is selected or a field is focused.
        closeAll({ endReason: ACTIVITY_END_REASON.CONTEXT_SWITCH });
        return;
    }

    const context = yield* readOperationalContext(transaction);
    switchTo({
        transactionId: transaction.id,
        pageKey: resolvePageKey({ isSplitViewMode, transaction }),
        context,
        endReason: ACTIVITY_END_REASON.CONTEXT_SWITCH,
    });
}

/**
 * Split view card selection, a SECONDARY trigger.
 *
 * `splitViewActiveTransactionId` is not the source of truth - the form that fired the focus event
 * is. This watcher exists because a card selection with no field focused still means the agent is
 * looking at that transaction, so it deserves a tab period. When a field is focused the form wins,
 * and `switchTo` is re-entrant on an already-open transaction so the two cannot fight.
 */
export function* onSplitViewActiveTransactionChanged() {
    const transactionId = yield select(selectSplitViewActiveTransactionId);

    if (!transactionId) {
        // Leaving split view, or clearing the selection, must not leave a period open.
        closeAll({ endReason: ACTIVITY_END_REASON.CONTEXT_SWITCH });
        return;
    }

    const transactions = yield select(selectTransactionsList);
    const transaction = transactions.find((candidate) => candidate?.id === transactionId) ?? null;
    const context = yield* readOperationalContext(transaction);

    switchTo({
        transactionId,
        pageKey: ACTIVITY_PAGE_KEY.TRANSACTIONS_SPLIT_VIEW,
        context,
        endReason: ACTIVITY_END_REASON.CONTEXT_SWITCH,
    });
}

/**
 * Every transaction close funnels through `closeTransactionPanel`, whose payload carries no reason
 * of its own - so the CALL SITE is the `end_reason` discriminator and each one now passes an
 * explicit `activityEndReason`. Inferring it here would be guessing, because by the time this runs
 * the store has already moved on.
 *
 * `closeAllPanels` is the desk-leave / logout shape: the agent stepped away from all of it.
 */
function* recordCompletedFieldReviews(transactionId) {
    if (transactionId === null || transactionId === undefined) return;

    const [fields, values, userEdited, transactions] = [
        yield select(selectFormConfigFields, transactionId),
        yield select(selectFormValues, transactionId),
        yield select(selectFormUserEdited, transactionId),
        yield select(selectTransactionsList),
    ];
    const transaction = transactions.find((candidate) => String(candidate?.id) === String(transactionId)) ?? null;
    const context = yield* readOperationalContext(transaction);
    const fieldTypes = buildFieldTypeMap(fields);
    const emitted = new Set();

    fields.forEach(({ dataField, header }) => {
        if (!dataField || emitted.has(dataField) || header === 'General' || dataField.startsWith('announcement.')) return;
        const value = values?.[dataField];
        if (value === undefined || value === null || value === '') return;

        emitted.add(dataField);
        recordFieldReviewCompleted({
            transactionId,
            fieldKey: dataField,
            fieldType: fieldTypes[dataField] ?? null,
            reviewResult: userEdited?.[dataField] ? ACTIVITY_FIELD_REVIEW_RESULT.CORRECTED : ACTIVITY_FIELD_REVIEW_RESULT.CONFIRMED,
            context,
        });
    });
}

export function* onCompletedReview({ payload }) {
    if (!payload?.activityReviewCompleted) return;
    yield* recordCompletedFieldReviews(payload?.transactionId);
    flushActivityQueue();
}

export const recordCompletedReviewsForClose = gatedWorker(function* recordReviews({ payload }) {
    yield* recordCompletedFieldReviews(payload?.transactionId);
    flushActivityQueue();
});

export function onCloseTransactionPanel({ payload }) {
    const endReason = payload?.activityEndReason ?? ACTIVITY_END_REASON.UNKNOWN;

    if (payload?.closeAllPanels) {
        closeAll({ endReason });
        // Desk leave and every other "all panels close" path - leaving the transaction page,
        // switching desk, losing the shared worker, a broker disconnect - tear the agent's whole
        // context down, and the architecture page puts desk leave in the same group as page unload
        // and logout: a normal XHR may not survive them, so this flush goes over a beacon.
        flushActivityQueue({ useBeacon: true });
        return;
    }

    closeTransactionPeriods({ transactionId: payload?.transactionId, endReason });
    // A single transaction completing is an ordinary in-app path reached AFTER the successful
    // completion response, not on the in-progress flag, so a normal request is fine here.
    flushActivityQueue();
}

/**
 * A deliberate logout is the one expiry the browser reliably detects while it can still submit the
 * event, so it is `session_expired` and it goes out over a beacon.
 *
 * A SERVER-SIDE expiry does not qualify and is not synthesized anywhere: we find out about it as a
 * 403 on a flush that already failed, and at that point the queue is lost. An open period is the
 * correct output there and it is not our bug.
 */
export function onLogoutStart() {
    transitionOperatorState(ACTIVITY_OPERATOR_STATE.OFF_DUTY, 'logout');
    closeAll({ endReason: ACTIVITY_END_REASON.SESSION_EXPIRED });
    flushActivityQueue({ useBeacon: true });
    stopOperatorHeartbeat();
}

/**
 * `field.value.applied` for a single-field write.
 *
 * Deduped to one intent per field per tick, then resolved against the field's RECORDED source
 * rather than against its value. That is what makes `blurHandler`'s formatting normalization
 * harmless: it dispatches a plain `setFieldValue` for a field the agent just edited, whose source
 * is therefore `'user'`, so it resolves to manual and marks the period dirty instead of inventing
 * an automation event.
 */
export function* onDirectValueWrite(action) {
    const write = readDirectWrite(action);
    if (!write) return;

    const { formId, fieldName } = write;

    // The two actions that ARE the agent's own edit stamp source 'user' in their own reducer, so
    // there is no sibling action to wait for - and marking the dirty bit synchronously matters:
    // a deferred mark could land after the blur that closes the period, losing the manual event
    // for the last character typed before tabbing away.
    if (isUserEditAction(action)) {
        markOpenFieldDirty({ transactionId: formId, fieldKey: fieldName });
        return;
    }

    // For the rest, let every action in this tick land before reading the source:
    // `copySheetAnnouncementHandler` dispatches `setFieldValue` and then `setFieldValueSource` for
    // the same field, and reading too early would see the previous source.
    yield delay(0);

    const sources = yield select(selectFormValueSources, formId);
    const source = sources?.[fieldName];

    if (source === USER_EDIT_SOURCE) {
        // A plain `setFieldValue` for a field the agent had already edited: this is
        // `blurHandler`'s formatting normalization, not an automation write. The manual event is
        // reported once at blur from the period's dirty bit, so ten keystrokes stay one event.
        markOpenFieldDirty({ transactionId: formId, fieldKey: fieldName });
        return;
    }

    // NO MEMO ON THIS SEAM, on purpose. A repeated single-field write is a repeated application and
    // deserves its own event - double-clicking a second sheet-announcement row really does re-apply
    // those fields. The duplicate this seam used to produce is fixed where it came from, by not
    // watching `setFieldValueSource`; see `directValueWriteActions`.
    //
    // Presence only, as a boolean, and the value never leaves this line. Without it a clearing write
    // reports an application: `copySheetAnnouncementHandler` nulls its `referenceField`, which has
    // no recorded source, so it would otherwise emit `automation` + `unknown` for a field that was
    // just emptied.
    const values = yield select(selectFormValues, formId);
    const value = values?.[fieldName];
    if (value === undefined || value === null || value === '') return;

    const transactions = yield select(selectTransactionsList);
    const transaction = transactions.find((candidate) => String(candidate?.id) === String(formId)) ?? null;
    const context = yield* readOperationalContext(transaction);
    const fieldType = yield* readFieldType(formId, fieldName);

    const { automationSource } = resolveInputSource(source);
    recordAutomationValueApplied({ transactionId: formId, fieldKey: fieldName, fieldType, automationSource, context });
    // Emitted on occurrence, but still recorded - so a bulk pass carrying this same source later
    // stays quiet instead of reporting the one applied value twice.
    rememberReportedSource(formId, fieldName, source);
}

/**
 * `field.value.applied` for a bulk automation pass.
 *
 * The reducers behind these actions apply values only in split view, and then only for the fields
 * that survive their own guards (a user-edited field an OCR pass must not override, equipment that
 * must not clobber an existing value, display-only "unknown" sentinels, and a dedup check). Rather
 * than mirroring those guards - which would drift the moment one changes - this watcher reports a
 * field when its RECORDED SOURCE has changed since we last reported it, which is true exactly once
 * per applied value. A repeated automation pass carrying already-applied fields reports nothing.
 */
const reportedSources = new Map();

function sourceMemoKey(formId, fieldName) {
    return `${formId}::${fieldName}`;
}

/**
 * One applied value, one event: reports a field only when its recorded source differs from what was
 * last reported for it. Read-and-write, used by the two BULK seams (`onBulkAutomation` and
 * `onAutoSuggestion`), both of which can re-run over fields they already applied.
 *
 * `has()` before comparing is not decoration. A field written by a handler that records no source
 * at all - the bison and conditionals autofills - has `source === undefined`, and an unseen memo
 * key also reads back `undefined`, so a bare `get(key) === source` would suppress the FIRST report
 * of exactly those writes.
 */
function shouldReportSourceChange(formId, fieldName, source) {
    const key = sourceMemoKey(formId, fieldName);
    if (reportedSources.has(key) && reportedSources.get(key) === source) return false;
    reportedSources.set(key, source);
    return true;
}

/**
 * WRITE WITHOUT READ, for the direct seam. This is what keeps the two seams in step.
 *
 * The direct seam deliberately does not consult the memo - a repeated single-field write is a
 * repeated application and earns its own event. But it has to LEAVE A MARK, because the bulk seams
 * do consult it: without this a direct write emits, the memo stays empty for that field, and the
 * next bulk pass carrying the same already-reported source reads "no entry, so the source changed"
 * and emits a second event for one applied value. `updateTransactionDataAutomation`
 * (`transactionsSagas.js:741`) is a real path that fires such a pass right after a copy handler.
 *
 * The reverse order needs it just as much: a bulk pass populates the memo, a direct write then
 * applies a NEW source, and unless that write updates the memo the following bulk pass sees a
 * change that has already been reported.
 */
function rememberReportedSource(formId, fieldName, source) {
    reportedSources.set(sourceMemoKey(formId, fieldName), source);
}

/**
 * `field_type` for an automation event, so the attribute is not present only on manual entries.
 *
 * Without this the fact store ends up asymmetric - `field_type` on every `manual`
 * `field.value.applied` (the state machine carries it on the open field period) and never on an
 * `automation` one, for no reason a query could explain.
 */
function* readFieldType(formId, fieldName) {
    const fields = yield select(selectFormConfigFields, formId);
    const field = fields.find((candidate) => candidate?.dataField === fieldName);
    return field ? resolveActivityFieldType(field) : null;
}

/**
 * `field.value.applied` (manual) for an Agent's change made outside the field, e.g. the step-by-step
 * protocol's Detected select writing `vehicle.transportType`. Skipped while that field's own period is
 * open: the value write already marked that period dirty, and its blur reports the change once.
 */
export function* onExternalManualValueApplied({ payload } = {}) {
    const { formId, fieldName } = payload ?? {};
    if (formId === undefined || formId === null || !fieldName) return;
    if (hasOpenFieldPeriodFor({ transactionId: formId, fieldKey: fieldName })) return;

    const transactions = yield select(selectTransactionsList);
    const transaction = transactions.find((candidate) => String(candidate?.id) === String(formId)) ?? null;
    const context = yield* readOperationalContext(transaction);
    const fieldType = yield* readFieldType(formId, fieldName);

    recordManualValueApplied({ transactionId: formId, fieldKey: fieldName, fieldType, context });
}

export function* onBulkAutomation(action) {
    const target = readBulkAutomationTarget(action);
    if (!target) return;

    yield delay(0);

    const { formId } = target;
    const sources = yield select(selectFormValueSources, formId);
    const values = yield select(selectFormValues, formId);
    const configFields = yield select(selectFormConfigFields, formId);
    const fieldTypes = buildFieldTypeMap(configFields);

    const transactions = yield select(selectTransactionsList);
    const transaction = transactions.find((candidate) => String(candidate?.id) === String(formId)) ?? null;
    const context = yield* readOperationalContext(transaction);

    Object.keys(sources).forEach((fieldName) => {
        if (VALUE_APPLIED_EXCLUDED_FIELDS.has(fieldName)) return;

        const source = sources[fieldName];
        // A manual edit is never reported from here; it belongs to the blur path.
        if (source === USER_EDIT_SOURCE) return;

        // The sentinel branch of `applyAutomationToFormValues` records a source WITHOUT writing a
        // value, so a field that ended up empty received nothing to report. This reads presence
        // only, as a boolean, and the value itself never leaves this line.
        const value = values?.[fieldName];
        const hasValue = value !== undefined && value !== null && value !== '';
        if (!hasValue) return;

        // Checked last, so a field skipped for having no value is not memoized as reported.
        if (!shouldReportSourceChange(formId, fieldName, source)) return;

        const { automationSource } = resolveInputSource(source);
        recordAutomationValueApplied({
            transactionId: formId,
            fieldKey: fieldName,
            fieldType: fieldTypes[fieldName] ?? null,
            automationSource,
            context,
        });
    });
}

/**
 * `field.value.applied` for the two timer-derived suggestion writes (Process Time, Exported to YMS).
 *
 * Same SOURCE-CHANGE rule as the bulk seam and the same memo, which is what makes these safe to
 * instrument at all: the suggestion fires repeatedly on a timer, and a repeat leaves the recorded
 * source on `'Auto'`, so only the first application reports. Reporting on occurrence instead would
 * emit one event per timer tick for a value that never changed.
 */
export function* onAutoSuggestion(action) {
    const target = readAutoSuggestionTarget(action);
    if (!target) return;

    yield delay(0);

    const { formId, fieldName } = target;
    const sources = yield select(selectFormValueSources, formId);
    const source = sources?.[fieldName];

    // The reducer bails out without writing when the field was user-edited or already held a
    // non-AUTO value, in which case the source is not ours to report.
    if (!source || source === USER_EDIT_SOURCE) return;

    const values = yield select(selectFormValues, formId);
    const value = values?.[fieldName];
    if (value === undefined || value === null || value === '') return;

    // Checked after the value, so a tick that wrote nothing is not memoized as reported.
    if (!shouldReportSourceChange(formId, fieldName, source)) return;

    const transactions = yield select(selectTransactionsList);
    const transaction = transactions.find((candidate) => String(candidate?.id) === String(formId)) ?? null;
    const context = yield* readOperationalContext(transaction);
    const fieldType = yield* readFieldType(formId, fieldName);

    const { automationSource } = resolveInputSource(source);
    recordAutomationValueApplied({ transactionId: formId, fieldKey: fieldName, fieldType, automationSource, context });
}

/**
 * `field.value.applied` for automation values the BACKEND already applied and persisted into the field
 * before the transaction arrives (e.g. a high-confidence alisa OCR read for `vehicle.transportType`,
 * where `transactionData.vehicle.transportType` already equals the automation value).
 *
 * Such a value is loaded as an unwatched initial value (`registerNewFormField`), so its source is never
 * recorded and, in tab view, the split-view apply seam never sees it - yet it is an effective, submitted
 * value. This reconciles `form.automation` against the persisted field value on arrival and reports the
 * ones the backend actually applied, in both tab and split view.
 *
 * Emit-only: it never writes form state. The persisted value is read ONLY to tell an applied value from a
 * mere suggestion (a field whose automation value did NOT land in the field is skipped); that value never
 * leaves this worker and never reaches the wire. Deduped against `onBulkAutomation` by the shared
 * `reportedSources` memo, so a split-view field the bulk seam already reported is not reported twice.
 */
export function* onArrivalAutomationApplied({ payload } = {}) {
    const formId = payload?.transactionId;
    if (formId === undefined || formId === null) return;

    // Let the arrival reducers settle (readAutomation, and the split-view apply the bulk seam also
    // watches) before reconciling, so an already-reported field is caught by the memo below.
    yield delay(0);

    const automation = yield select(selectFormAutomation, formId);
    const transactionData = yield select(selectFormTransactionData, formId);
    if (!transactionData || !automation) return;

    const userEdited = yield select(selectFormUserEdited, formId);
    const configFields = yield select(selectFormConfigFields, formId);
    const fieldTypes = buildFieldTypeMap(configFields);

    const transactions = yield select(selectTransactionsList);
    const transaction = transactions.find((candidate) => String(candidate?.id) === String(formId)) ?? null;
    const context = yield* readOperationalContext(transaction);

    Object.keys(automation).forEach((fieldName) => {
        if (VALUE_APPLIED_EXCLUDED_FIELDS.has(fieldName)) return;
        // A field the agent has touched belongs to the manual/blur path, never to automation.
        if (userEdited?.[fieldName]) return;

        const source = automation[fieldName]?.source;
        if (!source || source === USER_EDIT_SOURCE || source === AUTOMATION_SOURCE.AUTO) return;

        // The value the backend persisted into the field, read for comparison only.
        const effective = readFromObj(transactionData, fieldName);
        if (effective === PROPERTY_NOT_FOUND || effective === undefined || effective === null || effective === '') return;
        // Only a primitive leaf value can be an effective field value; an object cannot, and `String()`
        // would collapse two different objects to the same `[object Object]` and spuriously match.
        if (typeof effective === 'object') return;

        const originalValue = automation[fieldName]?.originalValue;
        if (originalValue === null || originalValue === undefined || typeof originalValue === 'object') return;

        // Applied by THIS automation only when the field holds exactly what the automation supplied;
        // otherwise it is a suggestion the backend did not write (or the agent replaced it).
        if (String(effective) !== String(originalValue)) return;

        if (!shouldReportSourceChange(formId, fieldName, source)) return;

        const { automationSource } = resolveInputSource(source);
        recordAutomationValueApplied({
            transactionId: formId,
            fieldKey: fieldName,
            fieldType: fieldTypes[fieldName] ?? null,
            automationSource,
            context,
        });
    });
}

/**
 * Export to YMS attempts (`yms_export.requested` / `yms_export.responded`).
 *
 * Hooked on `exportStarted` and not on the click: the Fenix saga dispatches it only after every guard
 * (fields complete, nothing pending, not already exported, one export per transaction), so a blocked
 * click never reaches here and is never counted. The reducer has already pushed the new pending
 * attempt by the time this runs, which `resolveYmsExportAttemptNumber` excludes.
 */
export function* onYmsExportStarted({ payload } = {}) {
    const transactionId = payload?.transactionId;
    if (transactionId === null || transactionId === undefined) return;

    const transactions = yield select(selectTransactionsList);
    const fenixEntry = yield select(selectFenixYmsEntry, transactionId);
    const transaction = transactions.find((item) => String(item?.id) === String(transactionId)) ?? null;
    const context = yield* readOperationalContext(transaction);

    beginYmsExportAttempt({ transactionId, attemptNumber: resolveYmsExportAttemptNumber(fenixEntry), context });
}

export function onYmsExportInitPending({ payload } = {}) {
    confirmYmsExportAttempt({ transactionId: payload?.transactionId, requestId: payload?.requestId });
}

/**
 * Every init failure closes its attempt with `result = error`: an error the backend answered with,
 * and equally a 400, a 5xx or a network failure that the backend did not record.
 */
export function onYmsExportInitFailed({ payload } = {}) {
    failYmsExportAttempt({ transactionId: payload?.transactionId });
    flushActivityQueue();
}

/**
 * A 409: another attempt was already pending or completed, so this click exported nothing. Its
 * `requested` was already sent on the click and stays without a pair.
 */
export function onYmsExportDiscarded({ payload } = {}) {
    cancelYmsExportAttempt({ transactionId: payload?.transactionId });
}

export function onYmsExportResponse({ payload } = {}) {
    completeYmsExportAttempt({ transactionId: payload?.transactionId, requestId: payload?.requestId, status: payload?.status });
    flushActivityQueue();
}

/**
 * A transaction closed before Fenix answered keeps its `requested` without a pair (by agreement, the
 * response is not chased after close). Ungated on purpose, like the flag watcher: a gate that shut
 * after an attempt began must not leave that attempt behind.
 */
export function onYmsExportCleanup({ type, payload } = {}) {
    if (type === authActions.logoutStart.type || payload?.closeAllPanels) {
        resetYmsExportTracker();
        return;
    }
    dropYmsExportAttempt({ transactionId: payload?.transactionId });
}

/** A closed form's memo would otherwise grow for the lifetime of the page. */
export function onFormRemoved({ payload: formId }) {
    const prefix = `${formId}::`;
    [...reportedSources.keys()].forEach((key) => {
        if (key.startsWith(prefix)) reportedSources.delete(key);
    });
}

/** One request actually sent to the face recognition service (BEAST-13916). */
export function* onFaceMatchRequested({ payload }) {
    const transactions = yield select(selectTransactionsList);
    const transaction = transactions.find((candidate) => String(candidate?.id) === String(payload?.transactionId));
    const context = yield* readOperationalContext(transaction);
    recordFaceMatchRequested({
        transactionId: payload?.transactionId,
        cameraPair: payload?.cameraPair ?? payload?.matchType,
        imageSource: payload?.imageSource,
        context,
    });
}

export function onFaceMatchSucceeded({ payload }) {
    recordFaceMatchSucceeded({ transactionId: payload?.transactionId, similarity: payload?.similarity });
}

export function onFaceMatchFailed({ payload }) {
    recordFaceMatchFailed({ transactionId: payload?.transactionId, error: payload?.error });
}

export function onFaceMatchCancelled({ payload }) {
    dropFaceMatchAttempt(payload?.transactionId);
}

/** Test seam only. */
export function resetActivityTrackingSagaState() {
    reportedSources.clear();
    resetOperatorStateTracker();
    resetYmsExportTracker();
    resetFaceMatchTracker();
}

export function* activityTrackingSaga() {
    yield all([
        // `safeWorker`, not `gatedWorker`: these have to run while the gate is still shut, since they
        // are what opens it. Feature-flag arrival recomputes the combined gate (global AND desk) and
        // drives operator-state (via onActivityFeatureFlagData); the current desk changing, an admin
        // toggling a desk's setting, and the all-desks broadcast (replaceDesk, BEAST-13284) recompute
        // the gate too. Moving from a disabled desk to an enabled one MUST run while the queue is off.
        takeEvery(featureFlagActions.setFeatureFlagData.type, safeWorker(onActivityFeatureFlagData)),
        takeEvery(
            [desksActions.setCurrentDesk.type, desksActions.setDeskActivityEventsEnabled.type, desksActions.replaceDesk.type],
            safeWorker(reconfigureActivityQueueGate)
        ),
        takeEvery(authActions.setUser.type, gatedWorker(onAuthenticatedUserChanged)),
        takeEvery(desksActions.setCurrentDesk.type, gatedWorker(onCurrentDeskChanged)),
        takeEvery(transactionActions.setStompBrokerConnectionState.type, gatedWorker(onStompConnectionStateChanged)),
        takeEvery(transactionActions.setTransactions.type, gatedWorker(onOperatorWorkStateChanged)),
        takeEvery(generalActions.setIsWindowDisconnectedFromSharedWorker.type, gatedWorker(onSharedWorkerStateChanged)),
        // Every action that can move `activeTab`, not just `setActiveTab` - see the comment on
        // `onActiveTabChanged` for why the ticket's "single funnel" is three funnels.
        takeEvery(
            [transactionActions.setActiveTab.type, transactionActions.handleNewTransaction.type, transactionActions.setVideoWallCameras.type],
            gatedWorker(onActiveTabChanged)
        ),
        takeEvery(
            [transactionActions.setSplitViewActiveTransaction.type, transactionActions.clearSplitViewActiveTransaction.type],
            gatedWorker(onSplitViewActiveTransactionChanged)
        ),
        // The window regained OS focus (agent came back from another desktop app). The lifecycle
        // listener closed the tab period on blur; reopen the current active transaction's period by
        // reusing `onActiveTabChanged`, which re-derives it and no-ops when it is still open.
        takeEvery(ACTIVITY_WINDOW_FOCUSED_ACTION, gatedWorker(onWindowFocused)),
        // Deliberately `takeEvery` and a separate watcher from the existing `onCloseTransactionPanel`
        // saga, which is `takeLatest`: a cancelled close must not lose its tracking events.
        takeEvery(transactionActions.closeTransactionPanel.type, gatedWorker(onCloseTransactionPanel)),
        takeEvery(transactionActions.closeTransactionPanel.type, gatedWorker(onCompletedReview)),
        takeEvery(authActions.logoutStart.type, gatedWorker(onLogoutStart)),
        takeEvery(directValueWriteActions(), gatedWorker(onDirectValueWrite)),
        takeEvery(ACTIVITY_EXTERNAL_MANUAL_VALUE_APPLIED_ACTION, gatedWorker(onExternalManualValueApplied)),
        takeEvery(bulkAutomationActions(), gatedWorker(onBulkAutomation)),
        // Automation values the backend already applied and persisted into the field on arrival (e.g. an
        // alisa OCR read for transportType). Reconciled against the persisted value so tab view reports
        // them too; deduped against `onBulkAutomation` by the shared memo.
        takeEvery(formControlActions.fetchTransactionFormConfigSuccess.type, gatedWorker(onArrivalAutomationApplied)),
        takeEvery(autoSuggestionActions(), gatedWorker(onAutoSuggestion)),
        // Gated too. Note the gate also shuts on a 401/403, after which this stops pruning and
        // whatever was memoized before it stays for the life of the page - a few hundred strings at
        // most, so it is not worth code, but it is not the "nothing to clean up" the flag-off case is.
        takeEvery(formControlActions.removeForm.type, gatedWorker(onFormRemoved)),
        // YMS export: `gatedWorker`, unlike face match below. The attempt number is read from the Fenix
        // slice, which keeps every attempt whether tracking is on or not, so the tracker has nothing to count
        // while the gate is shut. If the gate shuts after `begin`, the `responded` is not sent (the tracker's
        // own emit is gated as well) and the attempt waits in the tracker until the panel closes or logout.
        takeEvery(fenixYmsActions.exportStarted.type, gatedWorker(onYmsExportStarted)),
        takeEvery(fenixYmsActions.exportInitPending.type, gatedWorker(onYmsExportInitPending)),
        takeEvery(fenixYmsActions.exportInitFailed.type, gatedWorker(onYmsExportInitFailed)),
        takeEvery(fenixYmsActions.discardPendingExport.type, gatedWorker(onYmsExportDiscarded)),
        takeEvery(fenixYmsActions.exportResponseReceived.type, gatedWorker(onYmsExportResponse)),
        takeEvery([transactionActions.closeTransactionPanel.type, authActions.logoutStart.type], safeWorker(onYmsExportCleanup)),
        // `safeWorker`: the tracker counts attempts even with tracking off and decides itself what to emit, so
        // switching tracking on mid-transaction still numbers the next attempt right.
        takeEvery(faceMatchActions.compareFaces.type, safeWorker(onFaceMatchRequested)),
        takeEvery(faceMatchActions.compareFacesSuccess.type, safeWorker(onFaceMatchSucceeded)),
        takeEvery(faceMatchActions.compareFacesFailure.type, safeWorker(onFaceMatchFailed)),
        takeEvery(faceMatchActions.compareFacesCancelled.type, safeWorker(onFaceMatchCancelled)),
    ]);
}
